import React, { useCallback, useEffect, useState } from 'react';
import { Activity, Bell, Check, CheckSquare, Copy, FileUp, Folder, KeyRound, Mail, MessageSquare, Plus, ShieldCheck, UserRoundCog, Users, Wallet } from 'lucide-react';
import { useAuth } from '../../context';
import { apiCreateInternalUser, apiGetInternalUserActivity, apiGetInternalUsers, apiUpdateInternalUser } from '../../api';
import ModalShell from '../Modal/ModalShell';
import { UserIdentity } from '../People/PeopleUI';

const ROLE_LABELS = {
  admin: 'Admin',
  project_manager: 'Project Manager',
  production: 'Production',
};

const ROLE_HELP = {
  admin: 'Full portal access, including finances, mailer, Zoho, users, and protected deletions.',
  project_manager: 'Client work and read-only sales. No finances, mailer, Zoho, or protected deletions.',
  production: 'Client production work only. No sales, finances, mailer, Zoho, or protected deletions.',
};

const ACTIVITY_ICONS = {
  account: UserRoundCog,
  client_work: Activity,
  comment: MessageSquare,
  file: FileUp,
  project: Folder,
  milestone: Folder,
  todo: CheckSquare,
  upload_request: Bell,
  finance: Wallet,
  mailer: Mail,
};

const ACTIVITY_LABELS = {
  client_work: 'Client work',
  comment: 'Comment',
  file: 'File',
  project: 'Project',
  milestone: 'Milestone',
  todo: 'To-do',
  upload_request: 'Upload request',
  finance: 'Finance',
  mailer: 'Mailer',
  account: 'Account',
};

const formatActivity = (value) => {
  if (!value) return 'Never signed in';
  return new Date(value).toLocaleString();
};

export default function ManagerPage() {
  const { jwtToken, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activityUser, setActivityUser] = useState(null);
  const [activityItems, setActivityItems] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');
  const [form, setForm] = useState({ name: '', email: '', role: 'project_manager', password: '' });

  const loadUsers = useCallback(async () => {
    try {
      setUsers(await apiGetInternalUsers(jwtToken));
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jwtToken]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setCreated(null);
    try {
      const result = await apiCreateInternalUser(jwtToken, {
        ...form,
        password: form.password.trim() || undefined,
      });
      setCreated(result);
      setForm({ name: '', email: '', role: 'project_manager', password: '' });
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (account, role) => {
    setError('');
    try {
      const updated = await apiUpdateInternalUser(jwtToken, account.id, { role });
      setUsers((current) => current.map((item) => (item.id === account.id ? updated : item)));
    } catch (err) {
      setError(err.message);
    }
  };

  const copyPassword = async () => {
    if (!created?.generatedPassword) return;
    await navigator.clipboard.writeText(created.generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const openActivity = async (account) => {
    setActivityUser(account);
    setActivityItems([]);
    setActivityFilter('all');
    setActivityError('');
    setActivityLoading(true);
    try {
      const result = await apiGetInternalUserActivity(jwtToken, account.id);
      setActivityItems(result.activities || []);
    } catch (err) {
      setActivityError(err.message);
    } finally {
      setActivityLoading(false);
    }
  };

  const activityCategories = [...new Set(activityItems.map((item) => item.category))];
  const visibleActivity = activityFilter === 'all'
    ? activityItems
    : activityItems.filter((item) => item.category === activityFilter);

  return (
    <div className="manager-page">
      <div className="page-header manager-page-header">
        <div>
          <span className="workspace-eyebrow">Access control</span>
          <h1 className="page-title">Manager</h1>
          <p className="page-subtitle">Create internal accounts and assign portal access without changing client accounts.</p>
        </div>
      </div>

      <div className="manager-role-grid">
        {Object.entries(ROLE_LABELS).map(([role, label]) => (
          <div className="card manager-role-card" key={role}>
            <ShieldCheck size={18} />
            <strong>{label}</strong>
            <span>{ROLE_HELP[role]}</span>
          </div>
        ))}
      </div>

      <div className="manager-layout">
        <section className="card manager-create-card">
          <div className="manager-section-heading">
            <span className="manager-section-icon"><UserRoundCog size={18} /></span>
            <div><h2>Add internal user</h2><p>Leave password blank to generate a secure temporary password.</p></div>
          </div>

          <form className="popup-form" onSubmit={submit}>
            <div className="popup-field">
              <label className="field-label">Full name</label>
              <input className="input" value={form.name} onChange={(event) => setForm((value) => ({ ...value, name: event.target.value }))} required />
            </div>
            <div className="popup-field">
              <label className="field-label">Email</label>
              <input className="input" type="email" value={form.email} onChange={(event) => setForm((value) => ({ ...value, email: event.target.value }))} required />
            </div>
            <div className="popup-field">
              <label className="field-label">Access role</label>
              <select className="input" value={form.role} onChange={(event) => setForm((value) => ({ ...value, role: event.target.value }))}>
                {Object.entries(ROLE_LABELS).map(([role, label]) => <option value={role} key={role}>{label}</option>)}
              </select>
              <small className="manager-field-help">{ROLE_HELP[form.role]}</small>
            </div>
            <div className="popup-field">
              <label className="field-label">Temporary password (optional)</label>
              <input className="input" type="text" minLength={6} value={form.password} onChange={(event) => setForm((value) => ({ ...value, password: event.target.value }))} placeholder="Auto-generate" />
            </div>
            {error && <div className="popup-error">{error}</div>}
            <button className="btn btn-primary w-full" type="submit" disabled={saving}>
              <Plus size={16} /> {saving ? 'Creating…' : 'Create user'}
            </button>
          </form>

          {created && (
            <div className="manager-created-result">
              <Check size={17} />
              <div>
                <strong>{created.user.name} is ready</strong>
                <span>{created.generatedPassword ? 'Copy this password now; it is only shown once.' : 'The password you entered is active.'}</span>
                {created.generatedPassword && (
                  <button type="button" onClick={copyPassword}>
                    <KeyRound size={14} /><code>{created.generatedPassword}</code>{copied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        <section className="card manager-users-card">
          <div className="manager-section-heading">
            <span className="manager-section-icon"><Users size={18} /></span>
            <div><h2>Internal users</h2><p>{users.length} active account{users.length === 1 ? '' : 's'}</p></div>
          </div>

          {loading ? <div className="empty"><span className="spinner" /></div> : (
            <div className="manager-user-list">
              {users.map((account) => (
                <div
                  className="manager-user-row"
                  key={account.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openActivity(account)}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') openActivity(account); }}
                  aria-label={`View activity for ${account.name}`}
                >
                  <UserIdentity person={account} size="md" compact />
                  <div className="manager-user-copy">
                    <UserIdentity person={account} hideAvatar />
                    <span>{account.email}</span>
                    <small>Last active: {formatActivity(account.lastActiveAt)}</small>
                  </div>
                  <select
                    className="input manager-role-select"
                    value={account.role}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    onChange={(event) => changeRole(account, event.target.value)}
                    disabled={account.id === currentUser?.id}
                    aria-label={`Role for ${account.name}`}
                  >
                    {Object.entries(ROLE_LABELS).map(([role, label]) => <option value={role} key={role}>{label}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {activityUser && (
        <ModalShell
          title={`${activityUser.name}'s activity`}
          subtitle={`${activityUser.email} · ${ROLE_LABELS[activityUser.role] || activityUser.role}`}
          eyebrow="User history"
          icon={Activity}
          size="lg"
          onClose={() => setActivityUser(null)}
        >
          <div className="manager-activity-toolbar">
            <div><strong>{activityItems.length}</strong><span>recorded activities</span></div>
            {activityCategories.length > 1 && (
              <select className="input" value={activityFilter} onChange={(event) => setActivityFilter(event.target.value)}>
                <option value="all">All activity</option>
                {activityCategories.map((category) => <option value={category} key={category}>{ACTIVITY_LABELS[category] || category}</option>)}
              </select>
            )}
          </div>

          <div className="manager-activity-list scroll-hidden">
            {activityLoading ? (
              <div className="empty"><span className="spinner" /></div>
            ) : activityError ? (
              <div className="popup-error">{activityError}</div>
            ) : visibleActivity.length === 0 ? (
              <div className="manager-activity-empty"><Activity size={24} /><strong>No activity recorded yet</strong><span>New actions will appear here automatically.</span></div>
            ) : visibleActivity.map((item) => {
              const Icon = ACTIVITY_ICONS[item.category] || Activity;
              return (
                <div className="manager-activity-item" key={item.id}>
                  <span className={`manager-activity-icon ${item.category}`}><Icon size={15} /></span>
                  <div className="manager-activity-copy">
                    <strong>{item.summary}</strong>
                    <span>{ACTIVITY_LABELS[item.category] || item.category}</span>
                  </div>
                  <time title={new Date(item.createdAt).toLocaleString()}>{new Date(item.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</time>
                </div>
              );
            })}
          </div>
        </ModalShell>
      )}
    </div>
  );
}
